<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name: 'sketch', position: 'top', allowOtherLeft: true }"
    :button-bind="{ icon, tooltip }"
    :loading="loading"
    :target="target"
    unfill-width
  >
    <template #content>
      <div class="tw:p-2.5 tw:flexrow-1.5 tw:items-stretch">
        <div class="tw:flexcenter">
          <!-- DRAWING BUTTON -->
          <base-button
            icon="mdi-brush"
            type="text"
            :color="activeType === 'draw' ? 'primary' : undefined"
            :disabled="!visible"
            :tooltip="$i18n.t(`navbar.sketch.turn${activeType === 'draw' ? 'Off' : 'On'}Drawing`)"
            @click="activeType = activeType === 'draw' ? null : 'draw'"
          />
        </div>

        <!-- COLOR PICKER -->
        <div class="tw:flexcenter">
          <v-tooltip color="font" bottom>
            <template #activator="{ on }">
              <div v-on="on">
                <data-input v-model="color" :data-type="{ name: 'color' }" />
              </div>
            </template>
            {{ $i18n.t('navbar.sketch.color') }}
          </v-tooltip>
        </div>

        <!-- STROKE WIDTH NUMERIC INPUT -->
        <div class="tw:flexcenter">
          <div class="tw:max-w-[50px]">
            <v-tooltip color="font" bottom>
              <template #activator="{ on }">
                <div v-on="on">
                  <data-input
                    :value="stroke"
                    @change="$event => (stroke = parseInt($event))"
                    :data-type="{ name: 'integer' }"
                    class="tw:h-[25px] tw:[&_.v-input\_\_slot]:h-[25px] tw:[&_.v-input\_\_slot]:min-h-[25px]"
                    :min-value="1"
                    :max-value="10"
                    hide-arrows
                    hide-details
                  />
                </div>
              </template>
              {{ $i18n.t('navbar.sketch.stroke') }}
            </v-tooltip>
          </div>
        </div>

        <!-- STROKE WIDTH SLIDER INPUT -->
        <div class="tw:flexcenter">
          <v-tooltip color="font" bottom>
            <template #activator="{ on }">
              <div v-on="on" class="tw:max-w-[150px] tw:w-[150px] tw:flexcenter">
                <data-input
                  v-model="stroke"
                  class="tw:grow tw:[&_>_div_>_div]:pt-0 tw:[&_.v-input\_\_slot]:p-0"
                  :data-type="{ name: 'slider' }"
                  :min="1"
                  :max="10"
                  hide-value-label
                  hide-details
                />
              </div>
            </template>
            {{ $i18n.t('navbar.sketch.stroke') }}
          </v-tooltip>
        </div>

        <!-- ERASER -->
        <div class="tw:flexcenter">
          <base-button
            type="text"
            :color="activeType === 'erase' ? 'primary' : undefined"
            icon="mdi-eraser"
            :disabled="!visible"
            :tooltip="
              $i18n.t(activeType === 'erase' ? 'navbar.sketch.removeSketchesOff' : 'navbar.sketch.removeSketchesOn')
            "
            @click="activeType = activeType === 'erase' ? null : 'erase'"
          />
        </div>

        <!-- DIVIDER -->
        <div class="tw:bg-gray-200 tw:w-[1px]"></div>

        <!-- ACTION BUTTONS -->
        <div class="tw:flexcenter">
          <!-- VISIBILITY -->
          <base-button
            type="text"
            :color="visible ? 'primary' : undefined"
            :icon="visible ? 'mdi-eye' : 'mdi-eye-off'"
            :loading="refreshingTarget === 'visibility'"
            :disabled="refreshingTarget && refreshingTarget !== 'visibility'"
            :tooltip="$i18n.t(`navbar.sketch.${visible ? 'hide' : 'show'}Sketches`)"
            @click="visible = !visible"
          />

          <!-- ACTIONS MENU -->
          <dots-menu
            :items="[
              { name: 'refresh', disabled: refreshingTarget && refreshingTarget !== 'refreshing' },
              { name: 'clearAll', disabled: !drawings?.length },
            ]"
            @refresh="redrawSketches('refreshing')"
            @clearAll="eraseAllSketches"
          />

          <!-- MENU CLOSE -->
          <base-button
            type="text"
            icon="mdi-close"
            :tooltip="$i18n.t('navbar.sketch.close')"
            @click="isMenuOpen = false"
          />
        </div>
      </div>

      <!-- MAP POPOVER -->
      <sketch-popover
        v-if="identificationData"
        :coordinates="identificationData?.coordinates"
        :feature="identificationData?.feature"
        :target="target"
        :project-id="projectId"
        :layer-id="layerId"
        @onRemoved="onDrawingRemoved"
      />

      <!-- MULTIPLE SKETCH DELETE DIALOG -->
      <main-dialog
        v-if="isMultipleEraseDialogVisible && multipleEraseData"
        :is-visible.sync="isMultipleEraseDialogVisible"
        :title="$i18n.t('dialog.multipleDeleteSketch')"
        :body="
          $i18n.t(multipleEraseData?.all ? 'dialog.allDeleteSketchBody' : 'dialog.multipleDeleteSketchBody', {
            count: multipleEraseData?.ids?.length,
          })
        "
        agree-text="dialog.agreeDelete"
        @agree="eraseSketches"
        :loading="isMultipleEraseDialogLoading"
      />
    </template>
  </navbar-menu-tool>
</template>

<script>
import NavbarMenuTool from '@/components/NavbarMenuTool';
import DataInput from '@/components/DataInput';
import SketchPopover from '@/components/SketchPopover';
import MainDialog from '@/components/MainDialog';
import DotsMenu from '@/components/DotsMenu';

import sketchContext from '@/mixins/sketchContext.js';
import { getContext } from '@/assets/js/mapContext';

export default {
  name: 'TheNavbarToolsSketch',
  inject: {
    mapContext: {
      default: null,
    },
  },
  mixins: [sketchContext],
  components: {
    MainDialog,
    DotsMenu,
    NavbarMenuTool,
    DataInput,
    SketchPopover,
  },
  props: {
    icon: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
    target: {
      type: String,
    },
    projectId: {
      type: Number,
    },
    layerId: {
      type: Number,
    },
  },
  data: () => ({
    isMultipleEraseDialogVisible: false,
    isMultipleEraseDialogLoading: false,
    multipleEraseData: null,

    isMenuOpen: false,
    loading: false,

    activeType: null /** 'draw' | 'erase' */,
    visible: true,
    refreshingTarget: null,

    color: '#000',
    stroke: 2,

    drawings: [],

    identificationData: null,
  }),
  computed: {
    style() {
      return { color: this.color, stroke: this.stroke };
    },
  },
  watch: {
    /** Save currently changed style to localstorage */
    style(style) {
      localStorage.setItem('sketches-brush-color', style.color);
      localStorage.setItem('sketches-brush-stroke', style.stroke);
      getContext(this).$emit(
        'changeSketchStyle',
        style,
        async body => (this.drawings = await this.createContextSketch(body))
      );
    },

    /** Action on menu close and open */
    isMenuOpen(isOpened) {
      if (isOpened) {
        this.switchActiveTool();
      } else {
        this.identificationData = null;
        this.disableActions();
      }
    },

    /** Visibility manipulation */
    visible(isVisible) {
      if (!this.isMenuOpen) return;
      this.setStoreVisibilityPerContext();
      if (isVisible) {
        if (!this.activeType === 'draw')
          getContext(this).$emit('sketchIdentification-action', true, this.onSketchIdentified);
        this.redrawSketches('visibility');
      } else {
        this.activeType = null;
        this.identificationData = null;
        getContext(this).$emit('toggleSketchLayer');
      }
    },

    /** Drawing state manipulation */
    activeType(type) {
      if (!this.isMenuOpen) return;
      if (type === 'draw') localStorage.removeItem('sketches-drawing-deactivated');
      else localStorage.setItem('sketches-drawing-deactivated', 'true');
      this.switchActiveTool();
    },
  },
  methods: {
    async eraseSketches() {
      this.isMultipleEraseDialogLoading = true;
      try {
        this.drawings = await this.deleteContextSketch(this.multipleEraseData?.ids);
        getContext(this).$emit('toggleSketchLayer', this.drawings ?? []);
        this.identificationData = null;
        this.isMultipleEraseDialogVisible = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.isMultipleEraseDialogLoading = false;
      }
    },
    eraseAllSketches() {
      this.isMultipleEraseDialogVisible = true;
      this.multipleEraseData = {
        all: true,
        ids: this.drawings.map(draw => draw.id),
      };
    },
    onSketchEraseEnd(featuresToRemove) {
      if (!featuresToRemove.length) return;
      this.isMultipleEraseDialogVisible = true;
      this.multipleEraseData = {
        ids: featuresToRemove,
      };
    },
    onDrawingRemoved(newDrawings) {
      this.drawings = newDrawings;
      getContext(this).$emit('toggleSketchLayer', this.drawings ?? []);
      this.identificationData = null;
    },
    async redrawSketches(target) {
      this.refreshingTarget = target ?? 'none';
      this.identificationData = null;
      try {
        this.drawings = await this.getContextSketches();
        getContext(this).$emit('toggleSketchLayer', this.drawings ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        this.refreshingTarget = null;
      }
    },

    /** If menu is opened and sketches are visible on the map, there should be either identification or drawing enabled */
    switchActiveTool() {
      this.identificationData = null;
      if (this.activeType === 'draw') {
        getContext(this).$emit('sketchErase-action', false);
        getContext(this).$emit(
          'sketch-action',
          true,
          {
            color: this.color,
            stroke: this.stroke,
          },
          async body => (this.drawings = await this.createContextSketch(body))
        );
      } else if (this.activeType === 'erase') {
        getContext(this).$emit('sketch-action', false);
        getContext(this).$emit('sketchErase-action', true, this.onSketchEraseEnd);
      } else if (this.visible) {
        getContext(this).$emit('sketchIdentification-action', true, this.onSketchIdentified);
      } else {
        this.disableActions();
      }
    },
    onSketchIdentified(data) {
      this.identificationData = data.feature ? data : null;
    },

    /** Initial logic */
    setInitialVisibility() {
      this.visible = this.areSketchesVisiblePerContext();
      if (this.visible) this.redrawSketches();
    },
    setInitialDrawingState() {
      const drawingDeactivated = !!localStorage.getItem('sketches-drawing-deactivated');
      this.activeType = drawingDeactivated ? null : this.visible ? 'draw' : null;
    },
    setInitialStyle() {
      const brushColor = localStorage.getItem('sketches-brush-color');
      if (brushColor) this.color = brushColor;
      const brushStroke = parseInt(localStorage.getItem('sketches-brush-stroke'));
      if (brushStroke) this.stroke = brushStroke;
    },
    disableActions() {
      getContext(this).$emit('sketchErase-action', false);
      getContext(this).$emit('sketch-action', false);
      getContext(this).$emit('sketchIdentification-action', false);
    },
  },
  beforeDestroy() {
    getContext(this).$emit('toggleSketchLayer');
    this.disableActions();
  },
  created() {
    /** Get visibility from localstorage and parse drawings if it is not turned off */
    this.setInitialVisibility();

    /** Get drawing active state from localstorage  */
    this.setInitialDrawingState();

    /** Get draw parameters from localstorage */
    this.setInitialStyle();
  },
};
</script>
